import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Star } from "lucide-react";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { BrandWall } from "@/components/BrandWall";
import { GoogleRating } from "@/components/GoogleRating";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { usePageMeta } from "@/hooks/usePageMeta";
import { BRANDS, CONTACT, IMG, MANIFESTO, TESTIMONIALS, TILE_KINDS } from "@/data/site";

const BrandAnchors = () => (
  <section data-testid="brand-anchors-section" className="py-16 sm:py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
      <Reveal className="mb-14 flex flex-wrap items-end justify-between gap-6">
        <div>
          <Eyebrow>Bath Fittings First</Eyebrow>
          <h2 className="mt-4 max-w-xl font-serif text-2xl font-medium leading-tight tracking-tight text-foreground sm:text-3xl lg:text-4xl">
            Jaquar. Kohler. Hindware. <em className="text-brand-gradient not-italic">One address.</em>
          </h2>
        </div>
        <Link
          to="/brands"
          data-testid="brands-view-all-link"
          className="group flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-brand"
        >
          All Brands <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </Link>
      </Reveal>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
        {BRANDS.slice(0, 3).map((brand, i) => (
          <Reveal key={brand.id} delay={i * 0.12}>
            <Link
              to={`/brands/${brand.id}`}
              data-testid={`brand-card-${brand.id}`}
              className="group relative block overflow-hidden border border-ink/10 bg-white transition-colors duration-500 hover:border-brand/60"
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={brand.image}
                  alt={`${brand.name} luxury bathroom fittings`}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.08]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
              </div>
              <div className="absolute inset-x-0 bottom-0 p-6">
                <h3 className="font-serif text-3xl font-semibold text-[#F6F1E7]">{brand.name}</h3>
                <p className="mt-1 text-sm text-[#C7CFD8]">{brand.tagline}</p>
                <div className="mt-4 flex flex-wrap gap-2 opacity-0 transition-all duration-500 group-hover:opacity-100">
                  {brand.highlights.slice(0, 2).map((h) => (
                    <span key={h} className="border border-brand-bright/50 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-brand-bright">
                      {h}
                    </span>
                  ))}
                </div>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const TileCategories = () => (
  <section data-testid="tile-categories-section" className="bg-cream py-16 sm:py-24 lg:py-28">
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
      <Reveal className="mb-12 flex flex-wrap items-end justify-between gap-6">
        <div>
          <Eyebrow>We Also Stock Tiles</Eyebrow>
          <h2 className="mt-4 max-w-xl font-serif text-2xl font-medium leading-tight tracking-tight text-foreground sm:text-3xl lg:text-4xl">
            Tiles for <em className="text-brand-gradient not-italic">every surface.</em>
          </h2>
        </div>
        <p className="max-w-xs text-sm leading-relaxed text-[#5C6570]">
          Kajaria · Somany · Nitco · Kalinga Stone · AGL · Simpolo — full stock at the showroom.
        </p>
      </Reveal>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {TILE_KINDS.map((t, i) => (
          <Reveal key={t.name} delay={(i % 6) * 0.06}>
            <div
              data-testid={`tile-kind-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="group relative aspect-square overflow-hidden border border-ink/10 transition-colors duration-300 hover:border-brand/60"
            >
              <img
                src={t.image}
                alt={t.name}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-3 sm:p-4">
                <h3 className="font-serif text-base font-semibold text-[#F6F1E7] sm:text-lg">{t.name}</h3>
                <p className="mt-0.5 hidden font-mono text-[10px] uppercase tracking-[0.15em] text-[#C7CFD8] sm:block">
                  {t.desc}
                </p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-10 text-center">
        <a
          href={CONTACT.whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="tiles-whatsapp-button"
          className="group inline-flex items-center gap-3 border border-brand/60 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-brand transition-all duration-300 hover:bg-brand hover:text-paper"
        >
          Get Tile Prices on WhatsApp
          <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
        </a>
      </Reveal>
    </div>
  </section>
);

const Manifesto = () => (
  <section data-testid="manifesto-section" className="py-16 sm:py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
      <Reveal className="mb-16">
        <Eyebrow>Why Us</Eyebrow>
        <h2 className="mt-4 max-w-2xl font-serif text-2xl font-medium leading-tight tracking-tight text-foreground sm:text-3xl lg:text-4xl">
          Why Faridabad builds with <em className="text-brand-gradient not-italic">Shri Radhey Krishna.</em>
        </h2>
      </Reveal>

      <div className="grid grid-cols-1 gap-px bg-ink/10 lg:grid-cols-2">
        {MANIFESTO.map((ch) => (
          <Reveal key={ch.num} className="bg-paper">
            <div
              data-testid={`manifesto-chapter-${ch.num}`}
              className="group flex h-full flex-col gap-4 border border-transparent p-6 transition-colors duration-500 hover:border-brand/40 sm:flex-row sm:gap-8 sm:p-12"
            >
              <span className="font-serif text-5xl font-medium text-brand/30 transition-colors duration-500 group-hover:text-brand sm:text-6xl">
                {ch.num}
              </span>
              <div>
                <h3 className="font-serif text-xl font-semibold text-foreground sm:text-2xl">{ch.title}</h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-[#5C6570]">{ch.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Testimonials = () => (
  <section data-testid="testimonials-section" className="bg-cream py-16 sm:py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
      <Reveal className="mb-14 text-center">
        <Eyebrow>Client Stories</Eyebrow>
        <h2 className="mx-auto mt-4 max-w-2xl font-serif text-2xl font-medium leading-tight tracking-tight text-foreground sm:text-3xl lg:text-4xl">
          Trusted by homeowners, architects &amp; developers <em className="text-brand-gradient not-italic">across Faridabad.</em>
        </h2>
        <p className="mt-4 flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-brand">
          <Star size={13} fill="currentColor" /> 4.9 · Customer Rated
        </p>
      </Reveal>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.12}>
            <figure
              data-testid={`testimonial-${i}`}
              className="flex h-full flex-col border border-ink/10 bg-white p-6 transition-colors duration-500 hover:border-brand/50 sm:p-8"
            >
              <div className="flex gap-1 text-brand">
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star key={s} size={14} fill="currentColor" />
                ))}
              </div>
              <blockquote className="mt-5 flex-1 text-sm leading-relaxed text-[#3D4856]">“{t.text}”</blockquote>
              <figcaption className="mt-6 border-t border-ink/10 pt-5">
                <p className="font-serif text-lg font-semibold text-foreground">{t.name}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#8B8577]">{t.role}</p>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CtaBanner = () => (
  <section data-testid="cta-banner-section" className="relative overflow-hidden py-20 sm:py-28 lg:py-36">
    <div className="absolute inset-0">
      <img src={IMG.bathtub} alt="Freestanding luxury bathtub" loading="lazy" className="h-full w-full object-cover" />
      <div className="absolute inset-0 bg-ink/85" />
    </div>
    <div className="relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-8">
      <Reveal>
        <Eyebrow light>Visit The Showroom</Eyebrow>
        <h2 className="mt-6 font-serif text-3xl font-medium leading-tight tracking-tight text-[#F6F1E7] sm:text-4xl lg:text-5xl">
          Some fittings must be <em className="not-italic text-[#E8694F]">touched</em> to be believed.
        </h2>
        <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-[#C7CFD8]">
          Walk our gallery on Tigaon Road, Sector 86 — or call {CONTACT.owner} directly for
          today's best price on any brand.
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <Link
            to="/contact"
            data-testid="cta-book-visit-button"
            className="group flex items-center gap-3 bg-brand px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-paper transition-all duration-300 hover:bg-brand-bright"
          >
            Book a Visit <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-whatsapp-button"
            className="border border-[#F6F1E7]/40 px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] text-[#F6F1E7] transition-all duration-300 hover:border-brand-bright hover:text-brand-bright"
          >
            WhatsApp Us
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  usePageMeta(
    "Shri Radhey Krishna Bath and Tiles | Jaquar, Kohler, Hindware Showroom — Faridabad",
    "Authorised Jaquar, Kohler & Hindware bath fittings showroom in Sector 86, Greater Faridabad, with Kajaria, Somany & designer tiles. 50-year family legacy. Visit Tigaon Road today."
  );
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />
      <BrandAnchors />
      <BrandWall />
      <section data-testid="solutions-band" className="py-16 sm:py-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 sm:px-8 md:grid-cols-2 lg:px-16">
          {[
            {
              eyebrow: "Plumbing & Water",
              title: "Pipes & RO Water Systems",
              desc: "Astral and Fusion pipe systems, Kent RO purifiers and Zero B softeners — the hidden layer of every good bathroom.",
              to: "/pipes-ro",
              testId: "solutions-pipes-link",
            },
            {
              eyebrow: "Tile Installation",
              title: "Tile Adhesives & Grouts",
              desc: "MYK Laticrete, Roff, Dr. Fixit and Ardex Endura — the right grade for every tile.",
              to: "/tile-adhesives",
              testId: "solutions-adhesives-link",
            },
          ].map((s) => (
            <Link
              key={s.to}
              to={s.to}
              data-testid={s.testId}
              className="group relative overflow-hidden border border-brand/25 bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-brand/60 sm:p-10"
            >
              <span className="absolute left-0 top-0 h-px w-full shimmer-line" />
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand">{s.eyebrow}</p>
              <h3 className="mt-3 font-serif text-2xl font-semibold text-foreground">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[#5C6570]">{s.desc}</p>
              <span className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-brand">
                Explore <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </section>
      <TileCategories />
      <section data-testid="seo-info-section" className="py-16 sm:py-24">
        <div className="mx-auto max-w-3xl px-4 sm:px-8">
          <Reveal>
            <Eyebrow>Bathroom Fittings &amp; Tiles · Faridabad</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl font-medium leading-tight tracking-tight text-foreground sm:text-3xl">
              The showroom Greater Faridabad <em className="text-brand-gradient not-italic">searches for.</em>
            </h2>
            <p className="mt-6 text-base leading-relaxed text-[#5C6570]">
              Whether you're searching for a Jaquar dealer in Faridabad, a Kohler showroom
              near Sector 86, Hindware bathware on Tigaon Road, or a designer tiles shop in
              Neharpar — Shri Radhey Krishna Bath and Tiles is one address for all of it.
              Faucets, sanitaryware, showers, floor and wall tiles, imported basins and
              vanities, pipes, RO purifiers and tile adhesives, all with genuine billing.
            </p>
            <p className="mt-4 text-base leading-relaxed text-[#5C6570]">
              We deliver daily across Sectors 70 to 89, Greater Faridabad, Ballabhgarh and
              the wider NCR — and architects from Gurgaon to South Delhi send their
              Faridabad projects to us first.
            </p>
          </Reveal>
        </div>
      </section>
      <Manifesto />
      <Testimonials />
      <GoogleRating />
      <CtaBanner />
    </div>
  );
}
